/**
 * Aehnlichkeits-Warnung fuer Import-Positionen (Befund 31.07.2026, docs/BACKLOG.md, Auftrag
 * Punkt 2). Reine ANZEIGE im Review-Schritt: "Aehnlich zu bereits vorhandenem '{name}',
 * {preis}€ - pruefen?" - fuehrt NICHTS automatisch zusammen, aendert keine Daten, loescht nichts.
 *
 * Anlass war ein echter Beinahe-Fehler: dieselbe Speise wurde von der KI bei zwei Import-Laeufen
 * unterschiedlich transkribiert ("Steinpilzen-Ricotta „a la Vaccinara"" am 20.07. vs.
 * "Steinpilz-Ricotta „a la Vaccinara"" am 24.07.) - eine Gleichheits-Pruefung haette das nicht
 * erkannt, das Gericht waere als "Unikat ohne Preis" stehengeblieben, obwohl es laengst bepreist
 * im Katalog existierte.
 *
 * Metrik/Schwelle: normalisierte Levenshtein-Distanz (klassische DP, O(n*m)) - bewusst OHNE
 * weiteren KI-Aufruf (Auftrag: "guenstige Metrik reicht"). Eng kalibriert ("sehr aehnlich", nicht
 * "irgendwie aehnlich" - Auftrag: "lieber zu wenig Treffer melden als zu viele Fehlalarme"):
 *
 *   Ratio = 1 - Distanz / max(laenge A, laenge B); Schwelle 0.85.
 *
 * Nachgerechnet an den beiden Beispielen aus dem Befund (s. Tests):
 *   - "Steinpilz-Ricotta" vs. "Steinpilzen-Ricotta" (das tatsaechliche Namenspaar aus dem Befund,
 *     zwei KI-Transkriptionen derselben Speise, nur "en" eingefuegt, sonst identisch)
 *     -> Distanz 2, laengere Seite 19 Zeichen -> Ratio 0.895 -> TREFFER.
 *   - "Pizza Margherita" vs. "Pizza Marinara" -> Distanz 5, laengere Seite 16 Zeichen
 *     -> Ratio 0.688 -> KEIN Treffer.
 *  0.85 trennt beide sauber (Abstand zum Nicht-Treffer 0.16, zum Treffer 0.05) - eng genug, um
 *  "Pizza Margherita"/"Pizza Marinara" nicht zu melden.
 *
 * Zusaetzlich: exakte Gleichheit zaehlt NICHT als "aehnlich" (Auftrag: "SEHR AEHNLICHE, nicht
 * identische Position") - eine identische Position ist ein anderer Fall (Duplikat, kein
 * Warnhinweis dieser Art). Und sehr kurze Namen (< 4 Zeichen) werden ausgenommen: bei ihnen liefert
 * die Ratio-Formel zu leicht zufaellig hohe Werte (z. B. "Cola" vs. "Sola").
 */

export interface AehnlichkeitsKandidat {
  name: string;
  priceCents: number;
}

export const AEHNLICHKEITS_SCHWELLE = 0.85;
const MINDESTLAENGE = 4;

/** Normalisiert einen Positionsnamen fuer den Aehnlichkeits-Vergleich. */
export function normalisierePositionsname(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Klassische Levenshtein-Distanz (Editierdistanz) zwischen zwei Strings, einzeilige DP. */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const kosten = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j]! + 1, // Loeschen
        curr[j - 1]! + 1, // Einfuegen
        prev[j - 1]! + kosten, // Ersetzen
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length]!;
}

/** Normalisierte Aehnlichkeits-Ratio zweier (bereits normalisierter) Strings, 0..1. */
function ratio(a: string, b: string): number {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshtein(a, b) / maxLen;
}

/**
 * Sind `a` und `b` "sehr aehnlich" im Sinn der engen Schwelle oben - aber NICHT identisch?
 * Reine Pruef-Funktion, keine Seiteneffekte.
 */
export function istSehrAehnlich(a: string, b: string): boolean {
  const na = normalisierePositionsname(a);
  const nb = normalisierePositionsname(b);
  if (!na || !nb || na === nb) return false;
  if (Math.min(na.length, nb.length) < MINDESTLAENGE) return false;
  return ratio(na, nb) >= AEHNLICHKEITS_SCHWELLE;
}

/**
 * Findet unter `katalog` die aehnlichste, NICHT identische Position zu `name` (falls "sehr
 * aehnlich" gemaess `istSehrAehnlich`). Bei mehreren Treffern gewinnt die mit der hoechsten Ratio.
 * Reine Anzeige-Hilfe: liest nur, aendert/loescht/fuehrt nichts zusammen.
 */
export function findeAehnlichePosition<T extends AehnlichkeitsKandidat>(
  name: string,
  katalog: readonly T[],
): T | null {
  const na = normalisierePositionsname(name);
  if (!na || na.length < MINDESTLAENGE) return null;
  let bester: T | null = null;
  let besteRatio = 0;
  for (const kandidat of katalog) {
    const nb = normalisierePositionsname(kandidat.name);
    if (!nb || nb === na || nb.length < MINDESTLAENGE) continue;
    const r = ratio(na, nb);
    if (r >= AEHNLICHKEITS_SCHWELLE && r > besteRatio) {
      bester = kandidat;
      besteRatio = r;
    }
  }
  return bester;
}
